import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowCounterClockwise, CheckCircle, Circle, ShieldCheck, Warning } from '@phosphor-icons/react'
import { apiRequest, errorMessage, jsonBody } from '../api/client'
import type {
  ActivePlan,
  OwnerPlanV4LifecycleMutation,
  OwnerPlanV4Preview,
  OwnerPlanV4ReleaseGate,
} from '../api/types'
import { queryClient } from '../app/queryClient'

const activateConfirmation = 'AKTIVOVAT OWNER PLÁN V4'
const recoverConfirmation = 'OBNOVIT OWNER PLÁN V3'
const sha256Pattern = /^[a-f0-9]{64}$/i
const gateLabels: Record<string, string> = {
  DatabaseBackup: 'Ověřená databázová záloha',
  DotNetSuite: 'Kompletní .NET a PostgreSQL testy',
  FrontendSuite: 'React testy, lint a produkční build',
  MigrationPreview: 'Migrační preview a read-back',
  Security: 'Bezpečnostní a owner-isolation testy',
  BrowserMatrix: 'Browser matrix 375 / 834 / 1440 px',
  Offline: 'Offline hard refresh a reconnect',
  Worker: 'Worker exactly-once',
  RealOpenAi: 'Reálné OpenAI E2E přes DecisionPlan',
  RollbackRehearsal: 'Rollback a restore rehearsal',
}
const requiredGateKeys = Object.keys(gateLabels)
const dateFormatter = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

function shortHash(value: string | null): string {
  return value ? `${value.slice(0, 10)}…${value.slice(-6)}` : 'bez důkazu'
}

async function refreshLifecycleQueries() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['owner-v4-preview'] }),
    queryClient.invalidateQueries({ queryKey: ['active-plan'] }),
    queryClient.invalidateQueries({ queryKey: ['plans'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  ])
}

export function OwnerPlanV4LifecyclePanel({
  preview,
  activePlan,
}: {
  preview: OwnerPlanV4Preview
  activePlan: ActivePlan
}) {
  const [online, setOnline] = useState(() => navigator.onLine)
  const [backupReference, setBackupReference] = useState('')
  const [backupHash, setBackupHash] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [lastTransition, setLastTransition] = useState<OwnerPlanV4LifecycleMutation | null>(null)

  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])

  const gates = useQuery({
    queryKey: ['owner-v4-release-gates', preview.previewId, preview.stateVersion],
    queryFn: () => apiRequest<OwnerPlanV4ReleaseGate[]>(
      `/plans/owner-v4-preview/${preview.previewId}/release-gates`,
    ),
    enabled: preview.status === 'Ready' || preview.status === 'Activated',
    retry: false,
  })

  const activate = useMutation({
    mutationFn: () => apiRequest<OwnerPlanV4LifecycleMutation>(
      `/plans/owner-v4-preview/${preview.previewId}/activate`,
      {
        method: 'POST',
        body: jsonBody({
          expectedPreviewStateVersion: preview.stateVersion,
          expectedSourceActivationStateVersion: activePlan.stateVersion,
          backupReference: backupReference.trim(),
          backupHash: backupHash.trim(),
          confirmationText: confirmation,
        }),
      },
    ),
    onSuccess: async (result) => {
      setLastTransition(result)
      setConfirmation('')
      await refreshLifecycleQueries()
    },
  })

  const recover = useMutation({
    mutationFn: () => apiRequest<OwnerPlanV4LifecycleMutation>(
      `/plans/owner-v4-recovery/${activePlan.activationId}`,
      {
        method: 'POST',
        body: jsonBody({
          expectedActivationStateVersion: activePlan.stateVersion,
          backupReference: backupReference.trim(),
          backupHash: backupHash.trim(),
          confirmationText: confirmation,
        }),
      },
    ),
    onSuccess: async (result) => {
      setLastTransition(result)
      setConfirmation('')
      await refreshLifecycleQueries()
    },
  })

  if (preview.status !== 'Ready' && preview.status !== 'Activated') return null

  const gateByKey = new Map((gates.data ?? []).map((gate) => [gate.gateKey, gate]))
  const passedGateCount = requiredGateKeys.filter((key) => gateByKey.get(key)?.passed).length
  const allGatesPassed = passedGateCount === requiredGateKeys.length
  const validBackup = backupReference.trim().length > 0
    && backupReference.trim().length <= 500
    && sha256Pattern.test(backupHash.trim())
  const sourceMatches = activePlan.activationId === preview.transition.sourceActivationId
    && activePlan.sourcePlanId === preview.transition.sourcePlanId
    && activePlan.planInstanceId === preview.transition.sourcePlanInstanceId
    && activePlan.coachRolloutMode === 'Shadow'
  const activeV4Matches = activePlan.sourcePlanId === preview.sourcePlanId
    && activePlan.planInstanceId === preview.planInstanceId
  const canActivate = online
    && preview.status === 'Ready'
    && preview.transition.isReady
    && allGatesPassed
    && sourceMatches
    && validBackup
    && confirmation === activateConfirmation
  const canRecover = online
    && preview.status === 'Activated'
    && activeV4Matches
    && validBackup
    && confirmation === recoverConfirmation
  const mutationError = activate.error ?? recover.error

  return (
    <section className="owner-v4-lifecycle" aria-labelledby="owner-v4-lifecycle-title">
      <header className="owner-v4-lifecycle__heading">
        <ShieldCheck size={24} weight="duotone" />
        <div>
          <h3 id="owner-v4-lifecycle-title">Release a bezpečný návrat</h3>
          <p>Technické důkazy jsou pouze ke čtení. Aktivaci ani návrat nelze obejít běžným přepínačem plánů.</p>
        </div>
        <span className={`status-pill ${allGatesPassed ? 'status-pill--success' : 'status-pill--warning'}`}>
          {passedGateCount} / {requiredGateKeys.length} bran
        </span>
      </header>

      {gates.isPending && <p className="owner-v4-lifecycle__loading">Načítám release důkazy…</p>}
      {gates.isError && (
        <div className="owner-v4-lifecycle__error" role="alert">
          <Warning size={19} />
          <span>{errorMessage(gates.error)}</span>
          <button className="text-link" type="button" onClick={() => gates.refetch()}>Načíst znovu</button>
        </div>
      )}
      {gates.data && (
        <ol className="owner-v4-gates" aria-label="Release gates owner plánu v4">
          {requiredGateKeys.map((key) => {
            const gate = gateByKey.get(key)
            return (
              <li className={gate?.passed ? 'is-passed' : ''} key={key}>
                {gate?.passed ? <CheckCircle size={20} weight="fill" /> : <Circle size={20} />}
                <div>
                  <strong>{gateLabels[key]}</strong>
                  <span>{gate?.passed && gate.verifiedAt
                    ? `Ověřeno ${dateFormatter.format(new Date(gate.verifiedAt))}`
                    : 'Čeká na skutečný ověřovací důkaz'}</span>
                </div>
                <code title={gate?.evidenceHash ?? undefined}>{shortHash(gate?.evidenceHash ?? null)}</code>
              </li>
            )
          })}
        </ol>
      )}

      {preview.status === 'Ready' && (!preview.transition.isReady || !allGatesPassed || !sourceMatches) && (
        <div className="owner-v4-lifecycle__blocked" role="status">
          <Warning size={20} />
          <p>
            {!preview.transition.isReady
              ? 'Přechod je blokovaný rozpracovaným workoutem nebo neplatným read-backem.'
              : !allGatesPassed
                ? 'Aktivace se zobrazí až po doložení všech deseti skutečných release bran.'
                : 'Aktivní v3 nebo jeho Shadow stav už neodpovídá ověřenému preview.'}
          </p>
        </div>
      )}

      {preview.status === 'Ready' && preview.transition.isReady && allGatesPassed && sourceMatches && (
        <form className="owner-v4-transition-form" onSubmit={(event) => {
          event.preventDefault()
          if (canActivate) activate.mutate()
        }}>
          <div className="owner-v4-transition-form__copy">
            <strong>Aktivovat přesně tuto ověřenou v4 instanci</strong>
            <p>V4 začne v režimu Shadow. V3 se skryje až po úspěšném serverovém read-backu a zůstane rollback bodem.</p>
          </div>
          <label>
            <span>Reference ověřené zálohy</span>
            <input value={backupReference} maxLength={500} onChange={(event) => setBackupReference(event.currentTarget.value)} />
          </label>
          <label>
            <span>SHA-256 zálohy</span>
            <input value={backupHash} maxLength={64} spellCheck={false} onChange={(event) => setBackupHash(event.currentTarget.value)} />
          </label>
          <label>
            <span>Pro potvrzení napiš přesně: {activateConfirmation}</span>
            <input value={confirmation} autoComplete="off" spellCheck={false} onChange={(event) => setConfirmation(event.currentTarget.value)} />
          </label>
          {!online && <p className="inline-error">Aktivace je záměrně dostupná pouze online.</p>}
          <button className="button" type="submit" disabled={!canActivate || activate.isPending}>
            {activate.isPending ? 'Aktivuji…' : 'Aktivovat owner plán v4'}
          </button>
        </form>
      )}

      {preview.status === 'Activated' && (
        <form className="owner-v4-transition-form owner-v4-transition-form--recovery" onSubmit={(event) => {
          event.preventDefault()
          if (canRecover) recover.mutate()
        }}>
          <div className="owner-v4-transition-form__copy">
            <ArrowCounterClockwise size={22} />
            <div>
              <strong>Obnovit zachovaný owner plán v3</strong>
              <p>Recovery nic z v4 nemaže. Server odmítne návrat, pokud je rozpracovaný workout v4.</p>
            </div>
          </div>
          <label>
            <span>Reference ověřené zálohy</span>
            <input value={backupReference} maxLength={500} onChange={(event) => setBackupReference(event.currentTarget.value)} />
          </label>
          <label>
            <span>SHA-256 zálohy</span>
            <input value={backupHash} maxLength={64} spellCheck={false} onChange={(event) => setBackupHash(event.currentTarget.value)} />
          </label>
          <label>
            <span>Pro potvrzení napiš přesně: {recoverConfirmation}</span>
            <input value={confirmation} autoComplete="off" spellCheck={false} onChange={(event) => setConfirmation(event.currentTarget.value)} />
          </label>
          {!activeV4Matches && <p className="inline-error">Aktivní plán neodpovídá této v4 instanci.</p>}
          {!online && <p className="inline-error">Recovery je záměrně dostupné pouze online.</p>}
          <button className="button button--quiet" type="submit" disabled={!canRecover || recover.isPending}>
            {recover.isPending ? 'Obnovuji…' : 'Obnovit v3 z rollback bodu'}
          </button>
        </form>
      )}

      {lastTransition && (
        <p className="owner-v4-lifecycle__success" role="status">
          Přechod {lastTransition.transitionKind} byl potvrzen serverovým read-backem. Aktivní režim coache: {lastTransition.coachRolloutMode}.
        </p>
      )}
      {mutationError && <p className="inline-error" role="alert">{errorMessage(mutationError)}</p>}
    </section>
  )
}